// File: carModel
// Created: 2026-05-29
// Updated: 2026-05-29
// Author: Erick Hernández Silva

import type { CarConfig, Option, Overlay, Rule, RuleEffect, Screen, Step, SubOption } from './carWizardTypes';

export const slug = (name: string): string =>
  (name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'step';

export const normalizeChoiceName = (value: string): string =>
  (value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

const matchesName = (s: Step, name: string) =>
  (s.stepName || '').trim().toLowerCase() === name;

// Canonical legacy numbering by step name (mirrors CarWizardLegacy's
// normalizeStepNumbers). Stored stepNumbers drifted over time (e.g. steering is
// 14 in newer records), so legacy cars are renumbered before any detection.
const LEGACY_NAME_TO_NUMBER: Record<string, number> = {
  'engine': 1,
  'gearbox': 2,
  'exterior paint': 3,
  'front paint': 4,
  'indicators': 5,
  'bull bar + foglights': 6,
  'wheels': 7,
  'parachute hooks': 8,
  'side steps': 9,
  'exhaust pipes': 10,
  'interior color': 11,
  'seating setup': 12,
  'interior package': 13,
  'jerry can': 14,
  'steering wheel': 15,
};

const renumberLegacySteps = (steps: Step[]): Step[] =>
  steps.map((s) => {
    const mapped = LEGACY_NAME_TO_NUMBER[(s.stepName || '').trim().toLowerCase()];
    return mapped ? { ...s, stepNumber: mapped } : s;
  });

/** Legacy detectors — only used to derive the new model from old cars. */
const isLegacyInteriorColor = (s: Step) => s.stepNumber === 11 || matchesName(s, 'interior color');
const isLegacySeating = (s: Step) => s.stepNumber === 12 || matchesName(s, 'seating setup');
const isLegacyPackage = (s: Step) => s.stepNumber === 13 || matchesName(s, 'interior package');
const isLegacySteering = (s: Step) => s.stepNumber === 15 || matchesName(s, 'steering wheel');

const ensureStepIds = (steps: Step[]): Step[] => {
  const seen = new Set<string>();
  return steps.map((s) => {
    if (s.stepId && !seen.has(s.stepId)) {
      seen.add(s.stepId);
      return s;
    }
    let base = s.stepId || slug(s.stepName);
    let id = base;
    let i = 2;
    while (seen.has(id)) id = `${base}-${i++}`;
    seen.add(id);
    return { ...s, stepId: id };
  });
};

/**
 * Derive sub-option linking for legacy seating/steering options whose sub-options
 * match interior-color option names by position/name. Idempotent: skips options
 * that already declare subOptionsLinkedToStepId.
 */
const deriveLinking = (steps: Step[]): Step[] => {
  const interior = steps.find(isLegacyInteriorColor);
  if (!interior) return steps;

  const interiorByNorm = new Map<string, string>(); // normName -> optionId
  interior.options.forEach((o) => interiorByNorm.set(normalizeChoiceName(o.optionName), o.optionId));

  // Legacy contrast hint lives on the interior-package option.
  const pkg = steps.find(isLegacyPackage);
  const contrastSteeringName = pkg?.options
    .map((o) => o.contrastSteeringName)
    .find((v): v is string => !!v);

  const linkOption = (opt: Option, fixedName?: string): Option => {
    if (!opt.subOptions || opt.subOptions.length === 0) return opt;
    if (opt.subOptionsLinkedToStepId) return opt; // already new-model
    const fixedNorm = normalizeChoiceName(fixedName || '');
    const subOptions: SubOption[] = opt.subOptions.map((sub) => {
      if (sub.linkedOptionId) return sub;
      // The contrast variant becomes a "fixed" sub (no link) so the manual
      // selector can offer it; the rest follow the interior color.
      if (fixedNorm && normalizeChoiceName(sub.optionName) === fixedNorm) return sub;
      const linkedOptionId = interiorByNorm.get(normalizeChoiceName(sub.optionName));
      return linkedOptionId ? { ...sub, linkedOptionId } : sub;
    });
    return { ...opt, subOptionsLinkedToStepId: interior.stepId, subOptions };
  };

  return steps.map((s) => {
    if (isLegacySeating(s)) {
      return { ...s, options: s.options.map((o) => linkOption(o)) };
    }
    if (isLegacySteering(s)) {
      return {
        ...s,
        options: s.options.map((o) =>
          (o.optionName || '').trim().toLowerCase() === 'original'
            ? linkOption(o, contrastSteeringName)
            : o
        ),
      };
    }
    return s;
  });
};

// Legacy wizard grouping (the old hardcoded getUiStepEntries), by stepNumber.
const LEGACY_GROUPS: { name: string; nums: number[] }[] = [
  { name: 'Powertrain', nums: [1, 2] },
  { name: 'Exterior Color', nums: [3] },
  { name: 'Equipment', nums: [6, 4, 5, 8, 14] },
  { name: 'Stance', nums: [7, 9, 10] },
  { name: 'Upholstry', nums: [11] },
  { name: 'Seating', nums: [12] },
  { name: 'Dash', nums: [13, 15] },
];

/** A car is legacy when the API did not ship screens and steps carry stepNumber. */
export const isLegacyCar = (car: CarConfig): boolean =>
  (!car.screens || car.screens.length === 0) && (car.steps || []).some((s) => typeof s.stepNumber === 'number');

const legacyScreens = (steps: Step[]): Screen[] => {
  const byNum = new Map<number, Step>();
  steps.forEach((s) => { if (typeof s.stepNumber === 'number') byNum.set(s.stepNumber, s); });

  const screens: Screen[] = [];
  const placed = new Set<string>();
  LEGACY_GROUPS.forEach((g) => {
    const stepIds = g.nums.map((n) => byNum.get(n)?.stepId).filter((id): id is string => !!id);
    if (!stepIds.length) return;
    stepIds.forEach((id) => placed.add(id));
    screens.push({ screenId: slug(g.name), screenName: g.name, order: screens.length + 1, stepIds });
  });
  // Any step not covered by a group keeps the "exactly one screen" invariant.
  steps.forEach((s) => {
    if (placed.has(s.stepId)) return;
    screens.push({ screenId: s.stepId, screenName: s.stepName, order: screens.length + 1, stepIds: [s.stepId] });
  });
  return screens;
};

const ensureScreens = (steps: Step[], screens: Screen[] | undefined, legacy: boolean): Screen[] => {
  if (screens && screens.length > 0) return [...screens].sort((a, b) => a.order - b.order);
  if (legacy) return legacyScreens(steps);
  // Fallback (doc §9): one screen per step, in array order.
  return steps.map((s, i) => ({
    screenId: s.stepId,
    screenName: s.stepName,
    screenDescription: s.stepDescription,
    order: i + 1,
    stepIds: [s.stepId],
  }));
};

/**
 * Reproduce the old hardcoded behaviors as rules:
 * - side steps = Automatic electric → exhaust Hidden
 * - interior package with contrastSteeringName / contrastSeatingName → lock the
 *   matching variant (e.g. Contrast → Original steering wheel in Onyx/black)
 */
const legacyRules = (steps: Step[]): Rule[] => {
  const rules: Rule[] = [];
  const byNum = (n: number) => steps.find((s) => s.stepNumber === n);
  const nameIs = (value: string | undefined, name: string) => (value || '').trim().toLowerCase() === name;

  const side = byNum(9);
  const exhaust = byNum(10);
  const auto = side?.options.find((o) => nameIs(o.optionName, 'automatic electric'));
  const hidden = exhaust?.options.find((o) => nameIs(o.optionName, 'hidden'));
  if (side && exhaust && auto && hidden) {
    rules.push({
      ruleId: 'legacy-auto-electric-hides-exhaust',
      when: { selections: [{ stepId: side.stepId, optionIdIn: [auto.optionId] }] },
      then: [{ type: 'forceOption', targetStepId: exhaust.stepId, targetOptionId: hidden.optionId }],
    });
  }

  const pkg = byNum(13);
  const lockVariant = (target: Step | undefined, options: Option[], contrastName: string | undefined): RuleEffect[] => {
    const norm = normalizeChoiceName(contrastName || '');
    if (!target || !norm) return [];
    return options.flatMap((o) => {
      const sub = o.subOptions?.find((s) => normalizeChoiceName(s.optionName) === norm);
      return sub ? [{ type: 'forceSubOption' as const, targetStepId: target.stepId, targetOptionId: o.optionId, targetSubOptionId: sub.optionId }] : [];
    });
  };
  const steering = byNum(15);
  const seating = byNum(12);
  pkg?.options.forEach((pkgOpt) => {
    const then: RuleEffect[] = [
      ...lockVariant(steering, (steering?.options || []).filter((o) => nameIs(o.optionName, 'original')), pkgOpt.contrastSteeringName),
      ...lockVariant(seating, seating?.options || [], pkgOpt.contrastSeatingName),
    ];
    if (!then.length) return;
    rules.push({
      ruleId: `legacy-${slug(pkgOpt.optionName)}-locks-variants`,
      when: { selections: [{ stepId: pkg.stepId, optionIdIn: [pkgOpt.optionId] }] },
      then,
    });
  });

  return rules;
};

export const normalizeCar = (car: CarConfig): CarConfig => {
  const legacy = isLegacyCar(car);
  // Sub-option coupling is only *derived* (by name/stepNumber) for genuinely
  // legacy cars. New-format cars declare coupling explicitly via
  // subOptionsLinkedToStepId — never auto-couple them (their stepNumbers may not
  // follow the old fixed mapping, which would mis-link, e.g. to "jerry can").
  const withIds = ensureStepIds(legacy ? renumberLegacySteps(car.steps || []) : car.steps || []);
  const steps = legacy ? deriveLinking(withIds) : withIds;
  const screens = ensureScreens(steps, car.screens, legacy);
  const rules = car.rules && car.rules.length > 0
    ? car.rules
    : (legacy ? legacyRules(steps) : []);

  // Legacy cars often omit baseRenderOptionId; the old UI always rendered the
  // exterior paint (stepNumber 3) as base. Seed it so the base render appears.
  let baseRenderOptionId = car.baseRenderOptionId;
  if (legacy && !baseRenderOptionId) {
    const exterior = steps.find((s) => s.stepNumber === 3 || matchesName(s, 'exterior paint'));
    baseRenderOptionId = exterior?.options[0]?.optionId || baseRenderOptionId;
  }

  return { ...car, steps, screens, rules, baseRenderOptionId };
};

/* ---- lookup helpers (work on a normalized car) ---- */

export const indexStepsById = (car: CarConfig): Map<string, Step> =>
  new Map(car.steps.map((s) => [s.stepId, s]));

export const getStepById = (car: CarConfig, stepId: string): Step | undefined =>
  car.steps.find((s) => s.stepId === stepId);

export const getOptionById = (step: Step | undefined, optionId: string | undefined): Option | null =>
  step?.options.find((o) => o.optionId === optionId) ?? null;

/** stepId of the step that owns a given baseRenderOptionId, if any. */
export const findStepIdOfOption = (car: CarConfig, optionId: string): string | null => {
  for (const s of car.steps) {
    if (s.options.some((o) => o.optionId === optionId)) return s.stepId;
  }
  return null;
};

/** True for image placeholders that mean "no image of its own" (doc §8). */
export const isRenderableImage = (url?: string): url is string =>
  typeof url === 'string' && url.startsWith('http');

/* ---- layer ordering ---- */

// Sort keys for the preview stack. Step at index i sits at i + 1 (base render
// at 0); overlays slot in between. Callers sort by these and then assign integer
// ranks, since CSS z-index must be an integer.
export const SCENERY_LAYER_ORDER = -2;
export const SCENERY_SHADOW_LAYER_ORDER = -1;

export const overlayLayerOrder = (car: CarConfig, overlay: Overlay, baseStepId: string | null): number => {
  const tiebreak = Math.min(Math.max(overlay.order ?? 0, 0), 40) * 0.01;
  const { position, stepId } = overlay.placement || { position: 'top' };
  if (position === 'bottom') return 0.5 + tiebreak;
  if (position === 'afterStep' && stepId) {
    if (stepId === baseStepId) return 0.5 + tiebreak;
    const idx = car.steps.findIndex((s) => s.stepId === stepId);
    if (idx >= 0) return idx + 1.5 + tiebreak;
  }
  return car.steps.length + 2 + tiebreak;
};
